import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';

const mapContainerStyle = {
  width: '100%',
  height: '300px',
  borderRadius: '8px',
};

interface Coords {
  lat: number;
  lng: number;
}

const LocationPicker = ({
  onLocationChange,
  lat,
  lng,
}: {
  onLocationChange: (coords: Coords) => void;
  lat: number | null;
  lng: number | null;
}) => {
  const mapRef = useRef(null);
  const [markerPosition, setMarkerPosition] = useState<Coords | null>(
    lat && lng ? { lat, lng } : null,
  );

  // Efecto para sincronizar las props con el estado interno
  useEffect(() => {
    setMarkerPosition(lat && lng ? { lat, lng } : null);
  }, [lat, lng]);

  const position = useMemo(() => {
    return markerPosition || { lat, lng };
  }, [markerPosition, lat, lng]);

  const handleLocationUpdate = useCallback(
    (newLocation: Coords) => {
      setMarkerPosition(newLocation);
      if (onLocationChange) {
        onLocationChange(newLocation);
      }
    },
    [onLocationChange],
  );

  const handleMapClick = useCallback(
    (event: any) => {
      const _lat = event.latLng.lat();
      const _lng = event.latLng.lng();
      handleLocationUpdate({ lat: _lat, lng: _lng });
    },
    [handleLocationUpdate],
  );

  const handleMarkerDrag = useCallback(
    (event: any) => {
      const _lat = event.latLng.lat();
      const _lng = event.latLng.lng();
      handleLocationUpdate({ lat: _lat, lng: _lng });
    },
    [handleLocationUpdate],
  );

  const onLoad = useCallback((map: any) => {
    mapRef.current = map;
  }, []);

  // Validación mejorada
  if (
    !position?.lat ||
    !position?.lng ||
    Number.isNaN(position.lat) ||
    Number.isNaN(position.lng)
  ) {
    return <div>Las coordenadas iniciales no son válidas.</div>;
  }

  return (
    <div>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={{
          lat: position.lat,
          lng: position.lng,
        }}
        zoom={15}
        onLoad={onLoad}
        onClick={handleMapClick}
      >
        <Marker
          position={{
            lat: position.lat,
            lng: position.lng,
          }}
          draggable
          onDragEnd={handleMarkerDrag}
        />
      </GoogleMap>
      <div
        style={{
          marginTop: '8px',
          padding: '8px',
          backgroundColor: '#f0f0f0',
          borderRadius: '4px',
          fontSize: '11px',
          color: '#666',
          fontWeight: 'normal',
        }}
      >
        <strong>Coordenadas: </strong>
        {position.lat.toFixed(6)}, {position.lng.toFixed(6)}
      </div>
    </div>
  );
};

export default LocationPicker;
